<template>
  <div>
    <div class="d-flex align-center">
      <h2>{{ $t('features.nursery-web-attendance-list') }}</h2>
      <v-spacer />
      <v-btn
        @click="selectBtn"
        :loading="loading"
        :close-on-content-click="false"
        color="primary"
        variant="outlined"
        class="mr-4"
      >
        <v-icon start>mdi-tray-arrow-down</v-icon>
          {{ $t('common.download') }}
        <v-row class="selectDL">
          <v-btn
            @click="monthly"
            v-model="monthlyDownload"
            v-if="selectDownload"
            color="primary"
            variant="text"
            class="btnDL mt-4"
          >
            {{ $t('attendance.monthlyAttendance') }}
          </v-btn>
          <v-btn
            @click="daily"
            v-model="dailyDownload"
            v-if="selectDownload"
            color="primary"
            variant="text"
            class="btnDL mt-4"
          >
            {{ $t('attendance.dailyAttendance') }}
          </v-btn>
        </v-row>
      </v-btn>
      <v-btn
        @click="searchAttendance"
        :loading="loading"
        color="primary"
        variant="outlined"
      >
        <v-icon start>mdi-sync</v-icon>
        {{ $t('common.update') }}
      </v-btn>
    </div>

    <v-row>
      <date-pager v-model="searchFilters.date" class="pt-4"></date-pager>
    </v-row>

    <div class="d-flex align-bottom my-6">
      <div class="mr-8">
        <div class="font-weight-bold">{{ $t('attendance.currentChildCount') }}</div>
        <div class="mt-2 font-weight-bold">
          <span style="font-size: 40px">{{
            attendances ? (attendances.summary.presentChildrenCount - startCount) : 0
          }}</span>
          / {{ attendances ? attendances.summary.totalChildrenCount : 0 }}
        </div>
      </div>
      <div class="mr-8">
        <div class="font-weight-bold">{{ $t('attendance.absent') }}</div>
        <div class="mt-2 font-weight-bold">
          <span style="font-size: 40px">{{
            attendances ? attendances.summary.absentChildrenCount : 0
          }}</span>
        </div>
      </div>
    </div>

    <v-card variant="flat" class="mt-4 pa-2" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="3">
            <h5>{{ $t('common.class') }}</h5>
            <v-select
              v-model="searchFilters.classId"
              :items="classes"
              :item-title="(item) => `(${item.year}) ${item.className}`"
              item-value="classId"
              variant="outlined"
              hide-details
              density="compact"
              clearable
              :disabled="loading"
            ></v-select>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('attendance.attendanceStatus') }}</h5>
            <v-select
              v-model="searchFilters.status"
              :items="statusItems"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details
              density="compact"
              :disabled="loading"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('common.gender') }}</h5>
            <v-select
              v-model="searchFilters.gender"
              :items="genderItems"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details
              density="compact"
              :disabled="loading"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('common.age') }}</h5>
            <v-select
              v-model="searchFilters.age"
              :items="ageItems"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details
              density="compact"
              :disabled="loading"
              clearable
            ></v-select>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-table class="pt-4" v-if="attendances">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{ $t('attendance.childName') }}</th>
            <th class="text-left">{{ $t('attendance.attendanceStatus') }}</th>
            <th class="text-left">{{ $t('attendance.checkInTime') }}</th>
            <th class="text-left">{{ $t('attendance.checkOutTime') }}</th>
            <th class="text-left">{{ $t('attendance.feverPrediction') }}</th>
            <th class="text-left">{{ $t('attendance.className') }}</th>
            <th class="text-left">{{ $t('common.gender') }}</th>
            <th class="text-left">{{ $t('common.age') }}</th>
            <th class="text-left"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in attendances.attendances" :key="a.child.childId">
            <td>{{ a.child.lastName }} {{ a.child.firstName }}</td>
            <td>
              <div v-if="a.status == 'isAbsent'" style="color: deeppink;">
                {{ getStatusText(a.status) }}
              </div>
              <div v-else>
                {{ getStatusText(a.status) }}
              </div>
            </td>
            <td>{{ a.attendedAt ? formatDate(a.attendedAt) : "-" }}</td>
            <td>{{ a.leftAt ? formatDate(a.leftAt) : "-" }}</td>
            <td>
              <div v-if="a.isFeverAlert == 'FEVER'">
                <v-chip small color="error" label> {{ $t('attendance.fever') }} </v-chip>
              </div>

              <div v-else-if="a.isFeverAlert == 'MODEST_FEVER'">
                <v-chip small color="orange" text-color="white" label>
                  {{ $t('attendance.mildFever') }}
                </v-chip>
              </div>

              <div v-else>-</div>
            </td>
            <td>{{ getClassName(a.child) }}</td>
            <td>
              <v-chip
                v-if="a.child.gender == 'male'"
                color="blue"
                variant="tonal"
                dark
                small
                >{{ $t('common.male') }}</v-chip
              >

              <v-chip v-else color="pink lighten-2" variant="tonal" dark small>{{ $t('common.female') }}</v-chip>
            </td>
            <td>{{ $t('common.yearsOld', { n: getAgeBasedonBirthdate(a.child.dateOfBirth) }) }}</td>
            <td>
              <v-btn
                color="primary"
                variant="flat"
                small
                :to="`/attendance/${a.child.childId}/${a.date}/edit`"
                :disabled="loading"
              >
                {{ $t('common.edit') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>

    <!--月次出席簿ダウンロード-->
    <v-dialog
      v-model="monthlyDownload"
      :close-on-content-click="false"
      max-width="645px"
    >
      <div class="d-flex align-center justify-center">
          <v-card class="DLMon">
            <v-card-title class="pb-6 download-title">{{ $t('attendance.downloadMonthly', { date: DLDate }) }}</v-card-title>
            <v-row class="pt-6">
              <v-btn
                @click="monthlyDownload = false"
                color="grey"
                style="color: white"
              >
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                @click="monthlyBtnDL"
                color="primary"
              >
                OK
              </v-btn>
            </v-row>
          </v-card>
      </div>
    </v-dialog>

    <!--日次出席簿ダウンロード-->
    <v-dialog
      v-model="dailyDownload"
      :close-on-content-click="false"
      max-width="625px"
    >
      <div class="d-flex align-center justify-center">
          <v-card class="DLDay">
            <v-card-title class="pb-6 download-title">{{ $t('attendance.downloadDaily', { date: DLDate }) }}</v-card-title>
            <v-row class="pt-6">
              <v-btn
                @click="dailyDownload = false"
                color="grey"
                style="color: white"
              >
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                @click="dailyBtnDL"
                color="primary"
              >
                OK
              </v-btn>
            </v-row>
          </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import DatePager from "@/components/atoms/DatePager.vue";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import {
  NurseryChildResponse,
  NurseryClassResponse,
  SearchNurseryAttendanceResponse,
} from "chaild-api/lib";
import { ageItems, genderItems, statusItems } from "@/constants/attendance";
import { attendanceModule } from "@/store/dataModules/attendanceModule";
import dayjs from "dayjs";
import CommonUtil from "@/utils/CommonUtil";

export default defineComponent({
  name: "AttendanceList",

  components: {
    DatePager,
  },

  watch: {
    searchFilters: {
      deep: true,
      async handler() {
        await this.searchAttendance();
      },
    },
    'searchFilters.date'(newDate, oldDate) {
      sessionStorage.setItem('attendancedate', newDate);
    },
    monthlyDownload(value) {
      if (value == false) {
        this.monthlyDownload = false;
      }
      if (this.monthlyDownload == false && this.dailyDownload == false) {
        this.selectDownload = false;
      }
    },
    dailyDownload(value) {
      if (value == false) {
        this.dailyDownload = false;
      }
      if (this.monthlyDownload == false && this.dailyDownload == false) {
        this.selectDownload = false;
      }
    },
  },

  async mounted() {
    const attendancedate = sessionStorage.getItem('attendancedate')
    const queries = this.$route.query
    if (queries.date) {
      const dateString = queries.date as string
      const date = dayjs(dateString).format('YYYY-MM-DD')
      this.searchFilters.date = date
    } else {
      const date = dayjs().format('YYYY-MM-DD')
      this.searchFilters.date = date
    }

    if (attendancedate) {
      this.searchFilters.date = attendancedate;
    }

    await this.searchAttendance();
    await this.listClasses();
  },

  data() {
    return {
      loading: false,
      searchFilters: {
        date: dayjs().format("DD-MM-YYYY"),
        age: "" as number | string,
        classId: undefined as string | undefined,
        gender: "" as string,
        status: "" as string,
        limit: 1000,
        skip: 0,
      },
      genderItems: genderItems,
      ageItems: ageItems,
      statusItems: statusItems,
      selectDownload: false,
      monthlyDownload: false,
      dailyDownload: false,
      DLDate: '',
    };
  },

  computed: {
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
    attendances: function (): SearchNurseryAttendanceResponse | null {
      return attendanceModule.attendances;
    },
    startCount: function (): number {
      if (this.attendances?.attendances) {
        let count = 0;
        for (let i = 0; i < this.attendances.attendances.length; i++) {
          if (this.attendances.attendances[i].leftAt) {
            count++;
          }
        }
        return count;
      }
      return 0;
    },
  },

  methods: {
    async listClasses() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule
          .listClass({
            nurseryId,
          })
          .catch();
      }
      this.loading = false;
    },
    async searchAttendance() {
      this.loading = true;
      this.selectDownload = false
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await attendanceModule
          .searchAttendance({
            nurseryId,
            ...this.searchFilters,
          })
          .catch();
      }
      this.loading = false;
    },
    formatDate(date: string | null | undefined) {
      if (date) {
        return dayjs(date).format("HH:mm");
      }
      return null;
    },
    getClassName(child: NurseryChildResponse) {
      if (child.nurseryClasses) {
        const year = CommonUtil.getFiscalYear(this.searchFilters.date);
        const currentClass = child.nurseryClasses.find(
          (c) => String(c.year) == year
        );
        if (currentClass) {
          return currentClass.className;
        }
      }
      return this.$t('common.unknownClass');
    },
    getAgeBasedonBirthdate(birthdate: string): number {
      const thisYear = dayjs();
      return thisYear.diff(birthdate, "year");
    },
    getStatusText(value: string): string {
      if (!value) {
        return "";
      }
      return statusItems.find((s) => s.value == value)?.text || "";
    },
    selectBtn() {
      if (this.selectDownload && this.monthlyDownload == false && this.dailyDownload == false) {
        this.selectDownload = false
      } else {
        this.selectDownload = true
      }
    },
    monthly() {
      this.monthlyDownload = true
      this.DLDate = dayjs(this.searchFilters.date).format('MM/YYYY')
    },
    daily() {
      this.dailyDownload = true
      this.DLDate = dayjs(this.searchFilters.date).format('DD/MM/YYYY')
    },
    // 月次出席簿のダウンロード
    async monthlyBtnDL() {
      this.loading = true
      this.monthlyDownload = false
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await attendanceModule.DownloadAttendance({
          nurseryId: nurseryId,
          date: this.searchFilters.date,
          category: "attendance_summary"
        })
        this.loading = false
        return alert(this.$t('common.exportStarted'));
      }
      this.loading = false
      return alert(this.$t('common.exportFailed'));
    },
    // Daily attendance download
    async dailyBtnDL() {
      this.loading = true
      this.dailyDownload = false
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await attendanceModule.DownloadAttendance({
          nurseryId: nurseryId,
          date: this.searchFilters.date,
          category: "attendance"
        })
        this.loading = false
        return alert(this.$t('common.exportStarted'));
      }
      this.loading = false
      return alert(this.$t('common.exportFailed'));
    },
  },
});
</script>

<style lang="scss" scoped>
.filter-caption {
  font-size: 0.9rem;
}

.selectDL {
  top: 40px;
  position: absolute;
}

.btnDL {
  width: auto;
}

.DLMon {
  padding: 60px 88px;
  text-align: center;
}

.DLDay {
  padding: 60px 88px;
  text-align: center;
}

.download-title {
  white-space: normal;
  word-wrap: break-word;
}
</style>
